import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import useCrmSetup from "../hooks/useCrmSetup";
import useRole from "../hooks/useRole";
import { clientHref } from "../paths";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import CrmPageHeader from "../ui/CrmPageHeader";
import TodayStrip from "../dashboard/TodayStrip";
import NudgeList from "../dashboard/NudgeList";
import PerformancePanel from "../dashboard/PerformancePanel";
import FirstRunSetup from "../dashboard/FirstRunSetup";
import SetupBanner from "../dashboard/SetupBanner";
import { CRM } from "../../../constants/testIds/crm";

/**
 * Resolves the cross-module deep link `/admin/crm?contact=<email>&tab=…&pathway=…`
 * (the Pathways tracking dashboard's learner row, contract C1.05/C1.06 addenda): an exact
 * email lookup, then a REPLACE navigation to the client's record with every other
 * parameter kept. An unknown email says so and leaves the practitioner on the dashboard.
 */
function ContactDeepLink({ email }) {
  const t = useT("crm");
  const tc = useT("common");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const rest = new URLSearchParams(params);
    rest.delete("contact");
    crmApi.lookupClientByEmail(email)
      .then((hit) => navigate(clientHref(hit.client_id, rest.toString()), { replace: true }))
      .catch((e) => {
        toast.error(crmErrorMessage(e, t, tc, "errors.clientNotFound"));
        setParams(rest, { replace: true });
      });
  }, [email, navigate, params, setParams, t, tc]);
  return <p data-testid={CRM.dashContactLookup} className="text-sm text-eh-muted" aria-busy="true">{t("dash.openingClient")}</p>;
}

/**
 * Admin → CRM (spec Screen 1): three zones top to bottom — Today, Nudges, Performance —
 * or, with zero clients, the 3-card first-run setup. Each zone reads on its own and
 * fails on its own; a failed setup read still shows the three zones (it only costs the
 * first-run check and the banner).
 */
export default function CrmDashboard() {
  const t = useT("crm");
  const [params] = useSearchParams();
  const contact = params.get("contact");
  const { can } = useRole();
  const { setup, loadError, reload, enableReminders, dismissBanner } = useCrmSetup();

  if (contact) return <ContactDeepLink email={contact} />;

  const firstRun = setup && (setup.client_count || 0) === 0;
  const banner = setup?.banner?.show && can("manageEmail") ? setup.banner : null;

  return (
    <div data-testid={CRM.dash} className="space-y-8">
      <CrmPageHeader title={t("dash.title")} className="mb-0" />
      {loadError && <CrmLoadFailure error={loadError} onRetry={reload} testId="crm-setup-error" />}
      {banner && <SetupBanner banner={banner} onDismiss={dismissBanner} />}
      {firstRun ? (
        <FirstRunSetup setup={setup} onEnableReminders={enableReminders} onChanged={reload} />
      ) : setup || loadError ? (
        <>
          {/* Unknown (setup unread) is not "off": the server then decides on resend. */}
          <TodayStrip remindersOn={setup ? setup.cards?.reminders?.done !== false : true} />
          <NudgeList />
          <PerformancePanel />
        </>
      ) : null}
    </div>
  );
}
